import { useContext, useEffect, useState } from 'react';

import { Card } from '@gympass/yoga';
import { useQuery } from '@tanstack/react-query';
import {
  selectedDateAtom,
  selectedMonthAtom,
  selectedYearAtom,
} from 'atoms/dateSelector';
import { setMonth, setYear } from 'date-fns';
import withAuth from 'hocs/withAuth/withAuth';
import { useAtom, useAtomValue } from 'jotai';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { formatCurrency, getMaxAndMinDateLimit } from 'utils';

import { NotificationContext } from 'context';
import { useLogger } from 'hooks/useLogger/useLogger';
import { getInvoiceDetails } from 'services';
import { SpecialistRole } from 'services/specialist';

import {
  DateSelector,
  InvoiceBanner,
  InvoiceStatus,
  PaymentExtrasTable,
  PaymentSessionsTable,
  SessionsPaymentsTabList,
} from 'components';
import PageHeader from 'components/PageHeader';

import { maxDateSelection, minDateSelection } from './config';

import * as S from './styles';

const Payments: React.FC = () => {
  const intl = useIntl();
  const logger = useLogger();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isClosedInvoice, setClosedInvoice] = useState(true);

  const selectedYear = useAtomValue(selectedYearAtom);
  const selectedMonth = useAtomValue(selectedMonthAtom);
  const [selectedDate, setSelectedDate] = useAtom(selectedDateAtom);

  const { setNotification } = useContext(NotificationContext);

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getUTCFullYear();
  const paramYear = searchParams.get('year') as string;
  const paramMonth = searchParams.get('month') as string;

  useEffect(() => {
    setSearchParams(urlParams => {
      if (!paramYear) {
        urlParams.set('year', String(currentYear));
      }

      if (!paramMonth) {
        urlParams.set('month', String(currentMonth));
      }

      return urlParams;
    });

    if (paramYear && paramMonth) {
      const searchedDate = setYear(
        setMonth(new Date(), Number(paramMonth) - 1),
        Number(paramYear),
      );

      setSelectedDate(searchedDate);
      return;
    }

    setSelectedDate(currentDate);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { currenDateAfterLimit, currenDateBeforeLimit } =
      getMaxAndMinDateLimit(selectedDate, maxDateSelection, minDateSelection);

    if (currenDateAfterLimit || currenDateBeforeLimit) {
      setSelectedDate(currentDate);
      setSearchParams(urlParams => {
        urlParams.set('year', String(currentYear));
        urlParams.set('month', String(currentMonth));

        return urlParams;
      });
    }
  }, [
    currentDate,
    currentMonth,
    currentYear,
    selectedDate,
    setSearchParams,
    setSelectedDate,
  ]);

  const handleDateSelection = () => {
    setSearchParams(urlParams => {
      urlParams.set('year', String(selectedYear));
      urlParams.set('month', String(selectedMonth));

      return urlParams;
    });
  };

  const { data: invoice, isFetching: isLoadingInvoice } = useQuery({
    queryKey: [paramYear, paramMonth, 'invoiceDetails'],
    enabled: !!(paramYear && paramMonth),
    queryFn: () => getInvoiceDetails(Number(paramYear), Number(paramMonth)),
    onError: () => {
      logger.error('[Invoice][getInvoiceDetails]');
      setNotification({
        message: intl.formatMessage({
          id: 'CPPLATFORM.FEEDBACKGENERICERROR.TRYAGAINLATER',
        }),
        type: 'failure',
      });
    },
  });

  return (
    <S.Container>
      <PageHeader>
        <S.HeaderTitle>
          <FormattedMessage id="CPPAYMENTS.TABTITLE.SESSIONPAYMENTS" />
        </S.HeaderTitle>
      </PageHeader>
      <S.ContentWrapper>
        <S.SubHeaderContainer data-testid="payments-sub-header-container">
          <SessionsPaymentsTabList
            selectedIndex={2}
            year={paramYear}
            month={paramMonth}
          />
          <S.DatePaginationContainer>
            <InvoiceStatus
              year={Number(paramYear)}
              month={Number(paramMonth)}
              onChangeStatus={status => setClosedInvoice(status === 'closed')}
            />
            {paramYear && paramMonth && (
              <DateSelector
                minDate={minDateSelection}
                maxDate={maxDateSelection}
                onDateSelection={handleDateSelection}
              />
            )}
          </S.DatePaginationContainer>
        </S.SubHeaderContainer>
        {!isClosedInvoice && (
          <InvoiceBanner pendingMonth={Number(paramMonth)} />
        )}
        <S.StyledCard>
          <S.CardHeader isClosedInvoice={isClosedInvoice}>
            <FormattedMessage id="CPPAYMENTS.TITLE.PAYMENTS" />
          </S.CardHeader>
          <Card.Content>
            <PaymentSessionsTable
              isLoading={isLoadingInvoice}
              isClosedInvoice={isClosedInvoice}
              data={invoice?.products?.sessions || []}
              currencyCode={invoice?.currencyCode || ''}
              sessionsTotal={invoice?.summary?.sessions?.value}
              sessionsAmount={invoice?.summary?.sessions?.amount}
            />
          </Card.Content>
        </S.StyledCard>
        <S.StyledCard>
          <S.CardHeader isClosedInvoice={isClosedInvoice}>
            <FormattedMessage id="CPPAYMENTS.TITLE.EXTRA" />
          </S.CardHeader>
          <Card.Content>
            <PaymentExtrasTable
              isLoading={isLoadingInvoice}
              isClosedInvoice={isClosedInvoice}
              data={invoice?.products?.extras || []}
              currencyCode={invoice?.currencyCode || ''}
              extrasTotal={invoice?.summary?.extras?.value}
            />
          </Card.Content>
        </S.StyledCard>
        <S.TotalCard>
          <S.TotalWrapper>
            <div />
            <div />
            <S.TotalText>
              <FormattedMessage id="CPPAYMENTS.TEXT.TOTAL" />
            </S.TotalText>
            <S.TotalValue
              data-testid="payments-final-value"
              isClosedInvoice={isClosedInvoice}
            >
              {!isClosedInvoice || !invoice?.summary?.totalAmount
                ? '-'
                : formatCurrency(
                    invoice?.summary?.totalAmount,
                    invoice?.currencyCode || '',
                  )}
            </S.TotalValue>
          </S.TotalWrapper>
        </S.TotalCard>
      </S.ContentWrapper>
    </S.Container>
  );
};

export default withAuth(Payments, [SpecialistRole.CP]);
